import {
  Chip,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { hostname, parseError } from "../../utils";
import { AUDIO_SPEED_OPTIONS, DEFAULT_AUDIO_VOICE, DEFAULT_AUDIO_SPEED, DEFAULT_AUDIO_MODEL } from "../../constants";

export default ({
  actionParameters,
  setActionParameters,
  currentPlan,
  headers,
  existingRecord,
  setValidForm,
  getToken,
  companyId,
}: {
  actionParameters: any;
  setActionParameters: (p: any) => void;
  currentPlan: string;
  headers: Array<any>;
  existingRecord: boolean;
  setValidForm: (b: boolean) => void;
  getToken: Function;
  companyId: string;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [audioModels, setAudioModels] = useState<Array<any>>([]);
  const [voices, setVoices] = useState<Array<any>>([]);
  const inputRef = useRef<any>(null);

  useEffect(() => {
    setValidForm(actionParameters.prompt && actionParameters.model && actionParameters.voiceId);
  }, [actionParameters]);

  useEffect(() => {
    getAudioModels();
    getVoices();
  }, []);

  const getAudioModels = async () => {
    const currentToken = await getToken();
    try {
      const response = await axios.get(`${hostname}/api/v0/audio/models`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setAudioModels(response.data);
      // If no current model is selected and we have models, set the first one
      if (!actionParameters.model && response.data.length > 0) {
        setActionParameters({ ...actionParameters, model: response.data[0].id });
      }
    } catch (err) {
      console.error("Error loading audio models:", err);
    }
  };

  const getVoices = async () => {
    const currentToken = await getToken();
    try {
      const response = await axios.get(`${hostname}/audio/voices`, {
        params: {
          companyId,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setVoices(response.data.items || []);
      // If no voice is selected and we have voices, set the first one
      if (!actionParameters.voiceId && response.data.items && response.data.items.length > 0) {
        setActionParameters({ ...actionParameters, voiceId: response.data.items[0].voiceId });
      }
    } catch (err) {
      console.error("Error loading voices:", err);
    }
  };

  const handleInputChange = (event: any) => {
    const newValue = event.target.value;
    setActionParameters({ ...actionParameters, prompt: newValue });
    // Check for "/" and the cursor position

    if (
      inputRef.current &&
      newValue[inputRef.current.selectionStart - 1] === "/"
    ) {
      setAnchorEl(inputRef.current);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const selectedModel = audioModels.find((x) => x.id === actionParameters.model);

  return (
    <div>
      <div style={{ marginTop: "16px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Audio Model
          <Tooltip title="Flash models are faster and cheaper, ideal for shorter content. Standard models offer higher quality for longer audio. Check character limits for your content length.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Choose your ElevenLabs audio model. Different models have different character limits and durations.
        </Typography>
        <FormControl fullWidth style={{ marginTop: 8 }}>
          <InputLabel>Model</InputLabel>
          <Select
            label="Model"
            value={actionParameters.model || "eleven_flash_v2_5"}
            onChange={(e) =>
              setActionParameters({ ...actionParameters, model: e.target.value })
            }
          >
            {audioModels.map((audioModel, index) => (
              <MenuItem value={audioModel.id} key={index}>
                {audioModel.name}
                <Chip
                  label={`${audioModel.character_limit?.toLocaleString()} chars`}
                  size="small"
                  style={{ marginLeft: 8 }}
                  variant="outlined"
                />
                <Chip
                  label={`~${audioModel.duration_minutes} min`}
                  size="small"
                  style={{ marginLeft: 4 }}
                  variant="outlined"
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div style={{ marginTop: "16px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Audio Settings
          <Tooltip title="Voice: Select from public voices or your company's custom cloned voices. Speed: Adjust playback rate - slower for clarity, faster for energy.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Configure voice and speed settings for audio generation with ElevenLabs.
        </Typography>

        <FormControl fullWidth style={{ marginTop: 8 }}>
          <InputLabel>Voice</InputLabel>
          <Select
            label="Voice"
            value={actionParameters.voiceId || ""}
            onChange={(e) =>
              setActionParameters({ ...actionParameters, voiceId: e.target.value })
            }
          >
            {voices.length === 0 && (
              <MenuItem value="" disabled>
                No voices available
              </MenuItem>
            )}
            {voices.map((voice) => (
              <MenuItem value={voice.voiceId} key={voice.id}>
                {voice.name}
                {voice.companyId && (
                  <Chip
                    label="Company"
                    size="small"
                    style={{ marginLeft: 8 }}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {!voice.companyId && (
                  <Chip
                    label="Public"
                    size="small"
                    style={{ marginLeft: 8 }}
                    variant="outlined"
                  />
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth style={{ marginTop: 16 }}>
          <InputLabel>Speed</InputLabel>
          <Select
            label="Speed"
            value={actionParameters.speed || DEFAULT_AUDIO_SPEED}
            onChange={(e) =>
              setActionParameters({ ...actionParameters, speed: Number(e.target.value) })
            }
          >
            {[0.7, 0.8, 0.9, 1.0, 1.1, 1.2].map((value) => (
              <MenuItem key={value} value={value}>
                {value === 1.0 ? '1.0x (Normal)' : `${value}x`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div style={{ marginTop: "24px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Audio Text
          <Tooltip title="The text that will be converted to speech. Use {{Column Name}} to personalize with row data. Keep within the model's character limit for best results.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Enter the text you want converted to speech. Use column references
          to include data from the current row for personalized audio.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={6}
          placeholder="Hello {{First Name}}! This is a personalized message from {{Company Name}}. We're excited to connect with you about {{Position Title}} opportunities."
          variant="outlined"
          value={actionParameters.prompt || ""}
          onChange={handleInputChange}
          inputRef={inputRef}
          style={{ marginTop: "8px" }}
        />
        <Menu
          anchorEl={anchorEl}
          open={showDropdown}
          onClose={() => setShowDropdown(false)}
          sx={{ maxHeight: 300 }}
        >
          <MenuItem
            onClick={() => {
              setActionParameters({
                ...actionParameters,
                prompt:
                  (actionParameters.prompt || "").substring(
                    0,
                    (actionParameters.prompt || "").length - 1
                  ) + `{{Company Description}}`,
              });
              setShowDropdown(false);
            }}
          >
            Company Description
          </MenuItem>
          <MenuItem
            onClick={() => {
              setActionParameters({
                ...actionParameters,
                prompt:
                  (actionParameters.prompt || "").substring(
                    0,
                    (actionParameters.prompt || "").length - 1
                  ) + `{{Full Record}}`,
              });
              setShowDropdown(false);
            }}
          >
            Full Record
          </MenuItem>
          {headers.map((header, i) => (
            <MenuItem
              key={i}
              onClick={() => {
                setActionParameters({
                  ...actionParameters,
                  prompt:
                    (actionParameters.prompt || "").substring(
                      0,
                      (actionParameters.prompt || "").length - 1
                    ) + `{{${header.field}}}`,
                });
                setShowDropdown(false);
              }}
            >
              {header.field}
            </MenuItem>
          ))}
        </Menu>
        <Typography variant="caption" style={{ marginTop: "4px" }}>
          Type{" "}
          <span
            style={{
              backgroundColor: "lightgray",
              padding: "2px 4px",
              borderRadius: "4px",
              color: "black",
            }}
          >
            /
          </span>{" "}
          to insert row information or your Company Description
        </Typography>
      </div>
    </div>
  );
};